import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LuClock, LuPlus, LuSend, LuX } from 'react-icons/lu';

import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/utils/cn';
import { normalizeLocalDateTimeToServer } from '@/utils/date-formatter';
import { GENERIC_ERROR_MESSAGE } from '@/utils/error-utils';
import { AiChatQueryKeys, fetchConversationMessages, useConversations } from '@/views/ai-chat/ai-chat-queries';
import { useAiChatSidebarState } from '@/views/ai-chat/ai-chat-state';
import { useAiChatNewConversation } from '@/views/ai-chat/useAiChatNewConversation';
import { useCurrentPersonOrgGUID } from '@/views/auth/auth-queries';
import { API_ROOT } from 'env';

import { Message, MessageBubble } from './MessageBubble';
import { UserPromptContent } from './UserPromptContent';

/* eslint-disable no-console */
const debugAiChat = (message: string, payload?: unknown) => {
    if (!import.meta.env.DEV) {
        return;
    }

    if (payload === undefined) {
        console.debug('[AiChatSidebar]', message);
        return;
    }

    console.debug('[AiChatSidebar]', message, payload);
};
/* eslint-enable no-console */

const SUGGESTED_PROMPTS: { label: string; prompt: string }[] = [
    { label: 'Plan my day', prompt: 'Plan my day' },
    { label: 'Prep my meetings', prompt: 'Prep my meetings' },
    {
        label: 'Summarize outstanding tasks',
        prompt: 'Summarize my open and overdue tasks, grouped by household, and flag anything due in the next 7 days.',
    },
];
const AUTO_SCROLL_THRESHOLD_PX = 48;
// After a stream timeout or transport drop the backend keeps generating and persists the
// answer; poll the conversation until it lands instead of asking the user to reopen it.
const ANSWER_POLL_INTERVAL_MS = 10_000;
const ANSWER_POLL_MAX_ATTEMPTS = 30;
const STREAM_TIMEOUT_NOTICE =
    'This is taking longer than expected. I’m still working on it in the background and will show the answer here as soon as it’s ready.';
const STREAM_DROP_NOTICE =
    'The connection was interrupted, but I’m still working on it in the background and will show the answer here as soon as it’s ready.';

type ActiveStream = {
    keyRef: { current: string };
    es: EventSource;
    assistantMessageId: string;
};

function formatConversationDate(isoString: string): string {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export const AiChatSidebar: React.FC = () => {
    const { isOpen, close } = useAiChatSidebarState();
    const { data: personOrgGUID } = useCurrentPersonOrgGUID();
    const { conversationPrompt, clearConversationPrompt } = useAiChatNewConversation();
    const queryClient = useQueryClient();
    const [messagesByConvo, setMessagesByConvo] = useState<Record<string, Message[]>>({});
    const [activeConvoKey, setActiveConvoKey] = useState<string | null>(null);
    const [input, setInput] = useState('');
    const [streamConvoKeys, setStreamConvoKeys] = useState<Record<string, true>>({});
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const shouldAutoScrollRef = useRef(true);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const conversationIdRef = useRef<string | null>(null);
    const activeConvoKeyRef = useRef<string | null>(null);
    const activeStreamsRef = useRef<ActiveStream[]>([]);

    const messages = useMemo(
        () => (activeConvoKey ? (messagesByConvo[activeConvoKey] ?? []) : []),
        [activeConvoKey, messagesByConvo],
    );
    const isViewedStreaming = activeConvoKey !== null && streamConvoKeys[activeConvoKey] === true;

    const { data: conversations = [], isFetching: isLoadingHistory } = useConversations(personOrgGUID, isHistoryOpen);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);

    const refreshStreamKeys = useCallback(() => {
        const keys: Record<string, true> = {};
        activeStreamsRef.current.forEach((s) => {
            keys[s.keyRef.current] = true;
        });
        setStreamConvoKeys(keys);
    }, []);

    const getScrollViewport = useCallback(() => {
        return scrollAreaRef.current?.querySelector<HTMLDivElement>('[data-radix-scroll-area-viewport]') ?? null;
    }, []);

    const isNearBottom = useCallback((scrollContainer: HTMLDivElement) => {
        return (
            scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight <=
            AUTO_SCROLL_THRESHOLD_PX
        );
    }, []);

    const scrollToBottom = useCallback(
        (force = false) => {
            const scrollContainer = getScrollViewport();
            if (!scrollContainer || (!force && !shouldAutoScrollRef.current)) {
                return;
            }

            scrollContainer.scrollTop = scrollContainer.scrollHeight;
        },
        [getScrollViewport],
    );

    useEffect(() => {
        const scrollContainer = getScrollViewport();
        if (!scrollContainer) {
            return;
        }

        // Keep the chat pinned only while the user is already reading the latest messages.
        const handleScroll = () => {
            shouldAutoScrollRef.current = isNearBottom(scrollContainer);
        };

        handleScroll();
        scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

        return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }, [getScrollViewport, isNearBottom, isHistoryOpen, isLoadingMessages]);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    useEffect(() => {
        if (isOpen && textareaRef.current) {
            setTimeout(() => textareaRef.current?.focus(), 150);
        }
    }, [isOpen]);

    const answerPollersRef = useRef<{ key: string; interval: ReturnType<typeof setInterval> }[]>([]);

    const cancelAnswerPollers = useCallback((conversationKey: string) => {
        answerPollersRef.current = answerPollersRef.current.filter((p) => {
            if (p.key !== conversationKey) {
                return true;
            }
            clearInterval(p.interval);
            return false;
        });
    }, []);

    const abortAllStreams = useCallback(() => {
        const pendingKeys: string[] = [];
        activeStreamsRef.current.forEach((s) => {
            s.es.close();
            if (s.keyRef.current.startsWith('__pending_')) {
                pendingKeys.push(s.keyRef.current);
            }
        });
        activeStreamsRef.current = [];
        setStreamConvoKeys({});
        answerPollersRef.current.forEach((p) => clearInterval(p.interval));
        answerPollersRef.current = [];
        if (pendingKeys.length > 0) {
            setMessagesByConvo((prev) => {
                const next = { ...prev };
                pendingKeys.forEach((k) => delete next[k]);
                return next;
            });
        }
    }, []);

    /**
     * The user message is persisted before the agentic loop starts, so the conversation's
     * last message flips to 'assistant' exactly when the pending answer is persisted.
     */
    const pollForPersistedAnswer = useCallback(
        (conversationGUID: string, assistantMessageId: string) => {
            let attempts = 0;
            const poller = setInterval(async () => {
                attempts += 1;
                const stopPolling = () => cancelAnswerPollers(conversationGUID);
                const patchAssistant = (updater: (msg: Message) => Message) => {
                    setMessagesByConvo((prev) => ({
                        ...prev,
                        [conversationGUID]: (prev[conversationGUID] ?? []).map((msg) =>
                            msg.id === assistantMessageId ? updater(msg) : msg,
                        ),
                    }));
                };
                try {
                    const data = await fetchConversationMessages(conversationGUID);
                    const last = data[data.length - 1];
                    if (last?.role === 'assistant') {
                        stopPolling();
                        debugAiChat('Recovered persisted answer after stream loss', { conversationGUID });
                        patchAssistant((msg) => ({
                            ...msg,
                            content: last.content,
                            parts: [{ type: 'token', content: last.content }],
                            timestamp: last.timestamp,
                            isStreaming: false,
                            isError: false,
                        }));
                        return;
                    }
                } catch (e) {
                    debugAiChat('Answer poll attempt failed, will retry', { conversationGUID, e });
                }
                if (attempts >= ANSWER_POLL_MAX_ATTEMPTS) {
                    stopPolling();
                    patchAssistant((msg) => ({
                        ...msg,
                        parts: [
                            ...msg.parts,
                            {
                                type: 'token',
                                content:
                                    '\n\n_I could not retrieve the answer here — please reopen this conversation from History in a little while._',
                            },
                        ],
                    }));
                }
            }, ANSWER_POLL_INTERVAL_MS);
            answerPollersRef.current.push({ key: conversationGUID, interval: poller });
        },
        [cancelAnswerPollers],
    );

    useEffect(() => {
        return () => abortAllStreams();
    }, [abortAllStreams]);

    const handleToggleHistory = useCallback(() => {
        setIsHistoryOpen((prev) => !prev);
    }, []);

    const handleLoadConversation = useCallback(
        async (id: string) => {
            shouldAutoScrollRef.current = true;
            setIsHistoryOpen(false);
            activeConvoKeyRef.current = id;
            setActiveConvoKey(id);
            conversationIdRef.current = id;
            setConversationId(id);
            const isStreamingTarget = activeStreamsRef.current.some((s) => s.keyRef.current === id);
            if (isStreamingTarget) {
                return;
            }
            setIsLoadingMessages(true);
            try {
                const data = await queryClient.fetchQuery({
                    queryKey: AiChatQueryKeys.messages(id),
                    queryFn: () => fetchConversationMessages(id),
                    staleTime: 0,
                });
                const loaded: Message[] = data
                    .filter((m) => m.role === 'user' || m.role === 'assistant')
                    .map((m) => ({
                        id: crypto.randomUUID(),
                        role: m.role as 'user' | 'assistant',
                        content: m.content,
                        timestamp: m.timestamp,
                        parts: m.role === 'assistant' ? [{ type: 'token' as const, content: m.content }] : [],
                    }));
                setMessagesByConvo((prev) => {
                    if (activeStreamsRef.current.some((s) => s.keyRef.current === id)) {
                        return prev;
                    }
                    return { ...prev, [id]: loaded };
                });
            } finally {
                setIsLoadingMessages(false);
            }
        },
        [queryClient],
    );

    const sendMessage = useCallback(
        (messageText: string) => {
            const trimmedMessage = messageText.trim();
            if (!trimmedMessage) {
                return;
            }
            const activeConversationId = conversationIdRef.current;
            const isSameConvoStreaming =
                activeConversationId !== null &&
                activeStreamsRef.current.some((s) => s.keyRef.current === activeConversationId);
            if (isSameConvoStreaming) {
                debugAiChat('Skipping sendMessage — same convo still streaming');
                return;
            }
            // A stale recovery poller from a lost stream must not keep running once a new
            // message is sent here: it could grab the NEW answer into the OLD bubble.
            if (activeConversationId) {
                cancelAnswerPollers(activeConversationId);
            }

            debugAiChat('sendMessage start', {
                messageText: trimmedMessage,
                conversationId: activeConversationId,
            });

            shouldAutoScrollRef.current = true;
            const userMessage: Message = {
                id: crypto.randomUUID(),
                role: 'user',
                content: trimmedMessage,
                parts: [],
            };

            const assistantMessageId = crypto.randomUUID();
            const assistantMessage: Message = {
                id: assistantMessageId,
                role: 'assistant',
                content: '',
                parts: [],
                isStreaming: true,
            };

            const tempKey = `__pending_${crypto.randomUUID()}`;
            const initialKey = activeConversationId ?? tempKey;
            const keyRef = { current: initialKey };

            setMessagesByConvo((prev) => ({
                ...prev,
                [initialKey]: [...(prev[initialKey] ?? []), userMessage, assistantMessage],
            }));

            if (!activeConversationId) {
                activeConvoKeyRef.current = tempKey;
                setActiveConvoKey(tempKey);
            }

            setInput('');

            const params = new URLSearchParams({ message: trimmedMessage });
            if (activeConversationId) {
                params.set('chatConversationGUID', activeConversationId);
            }

            const url = `${API_ROOT}/customer/chat/stream?${params.toString()}`;
            debugAiChat('Opening chat stream', { url });
            const es = new EventSource(url, { withCredentials: true });
            const stream: ActiveStream = { keyRef, es, assistantMessageId };
            activeStreamsRef.current.push(stream);
            refreshStreamKeys();
            let terminalEventHandled = false;
            // Flips on the first server-sent event (conversation_id always arrives first);
            // stays false when the request never reached a working stream (auth expiry, 5xx).
            let streamEstablished = false;

            const updateAssistant = (updater: (msg: Message) => Message) => {
                const key = keyRef.current;
                setMessagesByConvo((prev) => ({
                    ...prev,
                    [key]: (prev[key] ?? []).map((msg) => (msg.id === assistantMessageId ? updater(msg) : msg)),
                }));
            };

            const finishStream = () => {
                activeStreamsRef.current = activeStreamsRef.current.filter((s) => s !== stream);
                refreshStreamKeys();
                es.close();
            };

            const failTerminally = () => {
                updateAssistant((msg) => ({
                    ...msg,
                    content: msg.content || 'Something went wrong. Please try again.',
                    isStreaming: false,
                    isError: true,
                }));
            };

            // The backend outlives a lost stream and persists the answer — show a notice and
            // poll for it. Recovery needs proof the request actually reached the backend: a
            // real conversation GUID AND at least one received server event. Without both
            // (auth expiry, 5xx, stream never established) nothing is generating server-side
            // and polling would just dead-end, so surface a retryable error instead.
            const recoverInBackground = (notice: string) => {
                if (keyRef.current.startsWith('__pending_') || !streamEstablished) {
                    failTerminally();
                    return;
                }
                updateAssistant((msg) => ({
                    ...msg,
                    content: msg.content ? `${msg.content}\n\n${notice}` : notice,
                    parts: [...msg.parts, { type: 'token', content: `\n\n_${notice}_` }],
                    isStreaming: false,
                }));
                pollForPersistedAnswer(keyRef.current, assistantMessageId);
            };

            es.addEventListener('conversation_id', (event) => {
                debugAiChat('Received conversation_id', { conversationId: event.data });
                streamEstablished = true;
                const newId = event.data as string;
                const oldKey = keyRef.current;
                const wasPending = oldKey.startsWith('__pending_');
                const wasViewing = activeConvoKeyRef.current === oldKey;

                if (wasPending) {
                    setMessagesByConvo((prev) => {
                        const next = { ...prev };
                        if (next[oldKey]) {
                            next[newId] = next[oldKey];
                            delete next[oldKey];
                        }
                        return next;
                    });
                    keyRef.current = newId;
                    refreshStreamKeys();
                }

                if (wasViewing) {
                    if (wasPending) {
                        activeConvoKeyRef.current = newId;
                        setActiveConvoKey(newId);
                    }
                    conversationIdRef.current = newId;
                    setConversationId(newId);
                }
            });

            es.addEventListener('thinking', () => {
                updateAssistant((msg) => ({ ...msg, isThinking: true }));
            });

            es.addEventListener('token', (event) => {
                const { token }: { token: string } = JSON.parse(event.data);
                updateAssistant((msg) => {
                    const parts = [...msg.parts];
                    const last = parts[parts.length - 1];
                    if (last?.type === 'token') {
                        parts[parts.length - 1] = { type: 'token', content: last.content + token };
                    } else {
                        parts.push({ type: 'token', content: token });
                    }
                    return { ...msg, content: msg.content + token, parts, isThinking: false };
                });
            });

            es.addEventListener('tool_call', (event) => {
                const { tool, message }: { tool: string; message: string } = JSON.parse(event.data);
                updateAssistant((msg) => {
                    const parts = [...msg.parts];
                    const last = parts[parts.length - 1];
                    if (last?.type === 'tool_call') {
                        parts[parts.length - 1] = { type: 'tool_call', tool, message };
                    } else {
                        parts.push({ type: 'tool_call', tool, message });
                    }
                    return { ...msg, parts, isThinking: false };
                });
            });

            es.addEventListener('done', (event: MessageEvent) => {
                if (terminalEventHandled) {
                    return;
                }
                terminalEventHandled = true;
                const doneTimestamp = event.data || new Date().toISOString();
                debugAiChat('Received done event', { doneTimestamp });
                updateAssistant((msg) => ({
                    ...msg,
                    isStreaming: false,
                    timestamp: normalizeLocalDateTimeToServer(doneTimestamp),
                }));
                finishStream();
            });

            es.addEventListener('error', (event) => {
                if (terminalEventHandled) {
                    return;
                }
                terminalEventHandled = true;
                const rawError = (event as MessageEvent).data || '';
                debugAiChat('Received error event', { rawError });
                // A stream timeout is not a failure: the backend keeps working and persists the
                // answer into this conversation, so tell the user that instead of inviting a retry.
                if (rawError.includes('Chat stream timed out')) {
                    finishStream();
                    recoverInBackground(STREAM_TIMEOUT_NOTICE);
                    return;
                }
                // rawError may carry backend exception details — log it, never render it
                const errorMessage = rawError.includes('Max tool call rounds exceeded')
                    ? 'I could not finish this request within the allowed number of steps. Narrowing the question, for example to one household or date range, usually helps.'
                    : GENERIC_ERROR_MESSAGE;
                updateAssistant((msg) => ({ ...msg, content: errorMessage, isStreaming: false, isError: true }));
                finishStream();
            });

            es.onerror = () => {
                if (terminalEventHandled) {
                    debugAiChat('Ignoring EventSource onerror after terminal event');
                    return;
                }
                terminalEventHandled = true;
                debugAiChat('EventSource onerror fired');
                finishStream();
                recoverInBackground(STREAM_DROP_NOTICE);
            };
        },
        [refreshStreamKeys, pollForPersistedAnswer, cancelAnswerPollers],
    );

    const handleSubmit = useCallback(
        (e?: React.FormEvent) => {
            e?.preventDefault();
            sendMessage(input);
        },
        [input, sendMessage],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
            }
        },
        [handleSubmit],
    );

    const handleNewConversation = useCallback(() => {
        shouldAutoScrollRef.current = true;
        activeConvoKeyRef.current = null;
        setActiveConvoKey(null);
        conversationIdRef.current = null;
        setConversationId(null);
        setInput('');
        setIsHistoryOpen(false);
    }, []);

    // Start a new conversation with the conversation prompt
    useEffect(() => {
        debugAiChat('Conversation prompt effect', {
            isOpen,
            conversationPrompt,
        });

        if (isOpen && conversationPrompt) {
            debugAiChat('Consuming programmatic conversation prompt', {
                conversationPrompt,
            });
            handleNewConversation();
            sendMessage(conversationPrompt);
            clearConversationPrompt();
        }
    }, [isOpen, conversationPrompt, sendMessage, handleNewConversation, clearConversationPrompt]);

    const isEmpty = messages.length === 0 && !isLoadingMessages;

    const inputBox = (
        <div className="border-border rounded-2xl border bg-white">
            <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Find, write, or ask anything..."
                className="max-h-[140px] min-h-[60px] resize-none border-0 bg-transparent p-4 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
                disabled={isViewedStreaming}
            />
            <div className="flex items-center justify-end px-3 pb-3">
                <Button
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleSubmit()}
                    disabled={!input.trim() || isViewedStreaming}
                >
                    <LuSend className="h-3.5 w-3.5" />
                </Button>
            </div>
        </div>
    );

    return (
        <>
            {/* History panel — full overlay */}
            {isHistoryOpen && (
                <div className="absolute inset-0 z-10 flex flex-col bg-white">
                    <div className="border-border flex h-[70px] flex-shrink-0 items-center justify-between border-b px-4">
                        <span className="text-foreground text-sm font-semibold">History</span>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground h-7 w-7"
                            onClick={handleToggleHistory}
                        >
                            <LuX className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                    <ScrollArea className="flex-1">
                        {isLoadingHistory ? (
                            <div className="text-muted-foreground p-4 text-center text-xs">Loading...</div>
                        ) : conversations.length === 0 ? (
                            <div className="text-muted-foreground p-4 text-center text-xs">No past conversations</div>
                        ) : (
                            <div className="flex flex-col py-1">
                                {conversations.map((conv) => (
                                    <button
                                        key={conv.chatConversationGUID}
                                        className={cn(
                                            'hover:bg-muted flex flex-col items-start gap-0.5 px-4 py-3 text-left transition-colors',
                                            conversationId === conv.chatConversationGUID && 'bg-muted',
                                        )}
                                        onClick={() => handleLoadConversation(conv.chatConversationGUID)}
                                    >
                                        <UserPromptContent
                                            content={conv.title}
                                            className="text-foreground line-clamp-2 text-sm font-medium"
                                        />
                                        <span className="text-muted-foreground text-xs">
                                            {formatConversationDate(conv.createdAt)}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </ScrollArea>
                    <div className="border-border border-t p-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-foreground w-full justify-start text-xs"
                            onClick={handleNewConversation}
                        >
                            <LuPlus className="mr-1.5 h-3.5 w-3.5" />
                            New conversation
                        </Button>
                    </div>
                </div>
            )}

            <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
                {isEmpty && !isHistoryOpen ? (
                    /* Empty state: full-height centered layout, no header */
                    <div className="flex flex-1 flex-col items-center justify-center px-8">
                        <div className="absolute top-3 right-3 flex items-center gap-1">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="text-muted-foreground hover:text-foreground h-8 w-8"
                                onClick={handleToggleHistory}
                                title="Conversation history"
                            >
                                <LuClock className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="text-muted-foreground hover:text-foreground h-8 w-8"
                                onClick={close}
                                title="Close"
                            >
                                <LuX className="h-4 w-4" />
                            </Button>
                        </div>
                        <img src="/logo-short-b.svg" width={80} className="mb-6 opacity-70" alt="Logo" />
                        <h2
                            className="text-foreground mb-6 text-xl leading-[0.9]"
                            style={{ fontFamily: 'ivypresto-headline, Georgia, serif' }}
                        >
                            What are we working on today?
                        </h2>
                        <div className="w-full max-w-md">
                            {inputBox}
                            <div className="mt-4 flex flex-wrap justify-center gap-2">
                                {SUGGESTED_PROMPTS.map(({ label, prompt }) => (
                                    <button
                                        key={label}
                                        className="cursor-pointer rounded-full bg-blue-50 px-4 py-2 text-sm font-light text-slate-600 transition-colors hover:bg-blue-100 dark:bg-blue-950/60 dark:text-slate-300 dark:hover:bg-blue-900/60"
                                        onClick={() => sendMessage(prompt)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Header — shown when there are messages or history is open */}
                        <div className="border-border flex h-[70px] flex-shrink-0 items-center justify-between border-b px-4">
                            <div className="flex items-center gap-2">
                                <span className="font-heading text-foreground text-base font-semibold">
                                    Claire&#8482;
                                </span>
                            </div>
                            <div className="flex items-center gap-1">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className={cn(
                                        'h-8 w-8',
                                        isHistoryOpen
                                            ? 'text-foreground'
                                            : 'text-muted-foreground hover:text-foreground',
                                    )}
                                    onClick={handleToggleHistory}
                                    title="Conversation history"
                                >
                                    <LuClock className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-muted-foreground hover:text-foreground h-8 w-8"
                                    onClick={handleNewConversation}
                                    title="New conversation"
                                >
                                    <LuPlus className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-muted-foreground hover:text-foreground h-8 w-8"
                                    onClick={close}
                                    title="Close"
                                >
                                    <LuX className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>

                        {/* Messages */}
                        {isLoadingMessages ? (
                            <div className="text-muted-foreground flex flex-1 items-center justify-center text-sm">
                                Loading conversation…
                            </div>
                        ) : (
                            <ScrollArea ref={scrollAreaRef} className="min-h-0 flex-1">
                                <div className="flex w-0 min-w-full flex-col gap-6 overflow-hidden p-4 pb-6">
                                    {messages.map((msg) => (
                                        <MessageBubble key={msg.id} message={msg} conversationId={conversationId} />
                                    ))}
                                </div>
                            </ScrollArea>
                        )}

                        {/* Input pinned to bottom */}
                        <div className="border-border flex-shrink-0 border-t p-3">{inputBox}</div>
                    </>
                )}
            </div>
        </>
    );
};
